import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { School, Hospital, TrainFront, ShoppingBag, Plane, Route as RouteIcon } from "lucide-react";

// Fix default marker icon (Leaflet + webpack quirk)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
    iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
    shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const POI_ICONS = {
    school:   School,
    hospital: Hospital,
    metro:    TrainFront,
    mall:     ShoppingBag,
    airport:  Plane,
    road:     RouteIcon,
};

const POI_COLORS = {
    school:   "#2563eb",
    hospital: "#dc2626",
    metro:    "#16a34a",
    mall:     "#d97706",
    airport:  "#9333ea",
    road:     "#0891b2",
};

// Derive a demo POI position around the property lat/lng based on distance
function poiPosition(baseLat, baseLng, distKm, idx) {
    // spread POIs radially so the map looks populated (positions are indicative)
    const angle = (idx * 45 + 15) * (Math.PI / 180);
    const dLat = (distKm / 111) * Math.cos(angle);
    const dLng = (distKm / (111 * Math.cos((baseLat * Math.PI) / 180))) * Math.sin(angle);
    return [baseLat + dLat, baseLng + dLng];
}

export default function PropertyMap({ lat, lng, pois = [], title }) {
    // Fallback default: New Delhi
    const center = [lat || 28.6139, lng || 77.2090];

    useEffect(() => {
        // Force resize after mount so the tiles render correctly in fluid layout
        setTimeout(() => window.dispatchEvent(new Event("resize")), 200);
    }, []);

    return (
        <div className="relative w-full h-[420px] md:h-[480px] border border-black/10 overflow-hidden" data-testid="property-map">
            <MapContainer
                center={center}
                zoom={13}
                scrollWheelZoom={false}
                style={{ width: "100%", height: "100%" }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {lat && lng ? (
                    <Marker position={center}>
                        <Popup>
                            <b>{title}</b>
                        </Popup>
                    </Marker>
                ) : null}
                {pois.map((p, i) => {
                    const pos = poiPosition(center[0], center[1], p.distance_km, i);
                    return (
                        <CircleMarker
                            key={i}
                            center={pos}
                            radius={7}
                            pathOptions={{
                                color: POI_COLORS[p.category] || "#c19a5b",
                                fillColor: POI_COLORS[p.category] || "#c19a5b",
                                fillOpacity: 0.85,
                                weight: 2,
                            }}
                        >
                            <Popup>
                                <b>{p.name}</b><br/>
                                <span style={{ textTransform: "capitalize" }}>{p.category}</span> · {p.distance_km} km
                            </Popup>
                        </CircleMarker>
                    );
                })}
            </MapContainer>
        </div>
    );
}

// Legend/list companion component
export function POIList({ pois = [] }) {
    if (!pois.length) return null;
    const grouped = pois.reduce((acc, p) => {
        (acc[p.category] = acc[p.category] || []).push(p);
        return acc;
    }, {});
    return (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="poi-list">
            {Object.entries(grouped).map(([cat, items]) => {
                const Icon = POI_ICONS[cat] || RouteIcon;
                const color = POI_COLORS[cat] || "#c19a5b";
                return (
                    <div key={cat} className="border border-black/10 p-4 bg-white">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: color }}>
                                <Icon size={14}/>
                            </span>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-black">{cat}s</div>
                        </div>
                        <ul className="space-y-1.5 text-[13px]">
                            {items.map((p, i) => (
                                <li key={i} className="flex items-center justify-between text-black/75">
                                    <span className="truncate pr-2">{p.name}</span>
                                    <span className="tabular-nums text-black/45 text-[12px] shrink-0">{p.distance_km} km</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
}
